'use client';

import { useEffect, useRef, useMemo } from 'react';

import { useSearchParams } from '@/hooks/use-search-params.hook';
import { PersistedUserNavigationSearchParams } from '@/types/enums/persisted-user-navigation-search-params.enum';
import { slugify } from '@/utils/formatters/slugfy';

interface SetCityParamProps {
  citySlug?: string;
  urlSlug?: string;
}

function isValidCitySlug(slug: string | null): slug is string {
  if (!slug) {
    return false;
  }

  return /^[a-z0-9]+(-[a-z0-9]+)+$/.test(slug);
}

function extractCityFromUrlSlug(urlSlug: string): string | null {
  const parts = urlSlug.split('-em-');
  if (parts.length === 2) {
    const extracted = parts[1];
    const sanitized = slugify(extracted);
    return isValidCitySlug(sanitized) ? sanitized : null;
  }
  return null;
}

function sanitizeCitySlug(slug: string | undefined | null): string | null {
  if (!slug) {
    return null;
  }
  const sanitized = slugify(slug);
  return isValidCitySlug(sanitized) ? sanitized : null;
}

export function SetCityParam({ citySlug, urlSlug }: SetCityParamProps) {
  const searchParams = useSearchParams([PersistedUserNavigationSearchParams.CIDADE]);
  const hasSetParamRef = useRef(false);

  const resolvedCitySlug = useMemo(() => {
    if (citySlug) {
      return sanitizeCitySlug(citySlug);
    }
    if (urlSlug) {
      return extractCityFromUrlSlug(urlSlug);
    }
    return null;
  }, [citySlug, urlSlug]);

  useEffect(() => {
    if (!resolvedCitySlug || hasSetParamRef.current) {
      return;
    }

    const currentCityParam = searchParams.getParam(PersistedUserNavigationSearchParams.CIDADE);

    if (!currentCityParam) {
      hasSetParamRef.current = true;
      searchParams.setParams({
        [PersistedUserNavigationSearchParams.CIDADE]: resolvedCitySlug,
      });
    }
  }, [resolvedCitySlug, searchParams]);

  return null;
}
